import { FilePlus2, FileText, Package, Settings, Users } from 'lucide-react';
import { motion } from 'motion/react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { springSnappy } from '@/animations/variants';
import { Badge } from '@/components/ui/Badge';
import { LogoMark } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { BRAND } from '@/config/brand';
import { useBusiness } from '@/hooks/useBusiness';
import { useSettings } from '@/hooks/useSettings';
import { tokenAccentStyle } from '@/utils/accent';
import { cn } from '@/utils/cn';
import { BusinessSwitcher } from './BusinessSwitcher';

const NAV = [
  { to: '/app/new', label: 'New quote', icon: FilePlus2 },
  { to: '/app/quotes', label: 'Quotes', icon: FileText },
  { to: '/app/services', label: 'Services', icon: Package },
  { to: '/app/customers', label: 'Customers', icon: Users },
  { to: '/app/settings', label: 'Settings', icon: Settings },
] as const;

export function AppShell() {
  const { activeBusiness, template } = useBusiness();
  const { plan } = useSettings();

  return (
    <div
      data-accent
      style={tokenAccentStyle(template?.color ?? 'slate')}
      className="min-h-dvh bg-canvas lg:grid lg:grid-cols-[248px_minmax(0,1fr)]"
    >
      {/* Desktop sidebar */}
      <aside aria-label="Primary" className="hidden border-r border-hairline bg-sunken/50 lg:flex lg:h-dvh lg:flex-col lg:sticky lg:top-0">
        <div className="px-5 py-5">
          {/* The mark and the name are one link to the public homepage. A
              client-side route change: every provider sits above the router,
              so the session, the active business and the plan are untouched. */}
          <Link to="/" aria-label={`${BRAND.name} home`} className="flex w-fit items-center gap-2.5 rounded-lg">
            <LogoMark className="size-7" />
            <span className="text-[14px] font-semibold tracking-[-0.015em] text-ink">{BRAND.name}</span>
          </Link>
        </div>

        {/* The active business, as the control that switches it — separate
            from the brand link above, which still goes home. */}
        <BusinessSwitcher variant="sidebar" className="mx-3 mb-4" />

        <nav aria-label="Sections" className="flex flex-1 flex-col gap-0.5 px-3">
          {NAV.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>

        <div className="flex items-center justify-between gap-2 border-t border-hairline p-3">
          <Badge tone={plan.id === 'pro' ? 'accent' : 'neutral'}>{plan.name} plan</Badge>
          <ThemeToggle />
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-hairline bg-canvas/85 px-4 py-3 backdrop-blur-xl lg:hidden">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* The mark is the home link here too. The business name beside it
              is not the brand: it is the business switcher. */}
          <Link to="/" aria-label={`${BRAND.name} home`} className="flex shrink-0 rounded-lg">
            <LogoMark className="size-7" />
          </Link>
          <BusinessSwitcher variant="bar" />
        </div>
        <ThemeToggle />
      </header>

      <div id="main-content" tabIndex={-1} className="pb-24 lg:pb-0">
        {/* Keyed on the business: switching remounts the page, so nothing the
            previous business had open — a form, a dialog, a search — carries over. */}
        <Outlet key={activeBusiness?.id} />
      </div>

      {/* Mobile bottom nav */}
      <nav aria-label="Sections" data-print-hide className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-hairline bg-canvas/90 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur-xl lg:hidden">
        {NAV.map((item) => (
          <NavItem key={item.to} {...item} compact />
        ))}
      </nav>
    </div>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  compact = false,
}: {
  to: string;
  label: string;
  icon: typeof Package;
  compact?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
          compact && 'flex-col gap-1 px-1 py-1 text-[10px] font-normal',
          isActive ? 'text-accent-text' : 'text-muted hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !compact && (
            <motion.span
              layoutId="nav-pill"
              transition={springSnappy}
              className="absolute inset-0 rounded-lg bg-accent-soft"
            />
          )}
          <Icon className={cn('relative', compact ? 'size-[18px]' : 'size-4')} strokeWidth={2.1} />
          <span className="relative">{label}</span>
        </>
      )}
    </NavLink>
  );
}
